import React, { useState } from 'react'
import { Sparkles, AlertCircle, CheckCircle } from 'lucide-react'
import { Button, Modal } from '../common'
import useSurveyStore from '../../stores/surveyStore'
import { surveyApi } from '../../services/apiService'

export default function AnalyseButton({ surveyId, onAnalysisComplete }) {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState(null)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const markAnalysed = useSurveyStore(state => state.markAnalysed)

  const handleAnalyse = async () => {
    if (!currentSurvey || currentPhotos.length === 0) return

    // Check if there are photos marked for AI analysis
    const photosForAnalysis = currentPhotos.filter(p => p.analyseWithAI !== false)
    if (photosForAnalysis.length === 0) {
      setError('No photos marked for AI analysis')
      setShowModal(true)
      return
    }

    setLoading(true)
    setError(null)
    setShowModal(true)

    try {
      // Call Worker API to run analysis (API key stored server-side)
      const results = await surveyApi.analyse(currentSurvey.id)

      // Save analysis results to store
      await markAnalysed(results)

      setLoading(false)
      setShowModal(false)

      if (onAnalysisComplete) {
        onAnalysisComplete(results)
      }
    } catch (err) {
      console.error('Analysis failed:', err)
      setError(err.message || 'Analysis failed. Please try again.')
      setLoading(false)
    }
  }

  const canAnalyse = currentPhotos.length > 0 && currentPhotos.some(p => p.analyseWithAI !== false)

  return (
    <>
      <Button
        variant="accent"
        size="lg"
        icon="Sparkles"
        onClick={handleAnalyse}
        loading={loading}
        disabled={!canAnalyse}
        className="shadow-lg"
      >
        Analyse Photos
      </Button>

      {/* Analysis progress modal */}
      <Modal
        isOpen={showModal}
        onClose={() => !loading && setShowModal(false)}
        title="AI Analysis"
        size="md"
        closeOnOverlayClick={false}
        closeOnEscape={!loading}
      >
        <div className="space-y-6">
          {error ? (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysis Failed</h3>
              <p className="text-slate-600 text-center mb-4">{error}</p>

              {error.includes('API key') && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> The OpenAI API key may not be configured on the server.
                    Please contact your administrator.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                <Button variant="primary" onClick={handleAnalyse}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center py-6">
              <div className="relative mb-6">
                <div className="w-20 h-20 border-4 border-primary-100 rounded-full" />
                <div
                  className="absolute top-0 left-0 w-20 h-20 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"
                />
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-primary-600" />
              </div>

              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Analysing Photos...
              </h3>

              <p className="text-slate-600 mb-4">
                GPT-4 Vision is analysing your survey photos
              </p>

              <p className="text-sm text-slate-400 mt-4">
                This may take a few minutes...
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysis Complete</h3>
              <p className="text-slate-600 text-center">
                AI analysis has been completed successfully.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

// Status indicator showing if analysis is available
export function AnalysisStatus({ survey }) {
  if (!survey) return null

  if (survey.status === 'analysed') {
    return (
      <div className="flex items-center gap-2 text-green-600">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Analysed</span>
      </div>
    )
  }

  return null
}
